<script lang="ts">
import {
  computed,
  defineComponent,
  onBeforeUnmount,
  onMounted,
  reactive,
  toRefs,
  watch,
} from 'vue'
import { GDialog } from 'gitart-vue-dialog'
import { useToast } from 'vue-toastification'
import isEmpty from 'lodash/isEmpty'
import { useToggle } from '@vueuse/core'
import { useEnhancer } from '@/app/enhancer'
import { useCustomization } from '@/hooks/useCustomization'
import { actionTypes } from '@/hooks/enums'
import { downloadImage } from '@/utils/image'
import { useGtm } from '@/hooks/useGtm'
import { useDevices } from '@/hooks/useDevices'
import { QUESTIONNAIRE } from '@/configs/app.config'
import { EventType, useEvents } from '@/hooks/useEvents'
import ShareSnsAdapter from '@/transforms/share-sns'
import {
  ActionsBar,
  GuideCarousel,
  Layout,
  RootConfiguration,
  SlideTransition,
  ToolBar,
  TryOn,
  ZoomTool,
} from '@/components'
import CardHeader from '@/components/ui/card/CardHeader.vue'

export default defineComponent({
  components: {
    Layout,
    ToolBar,
    ZoomTool,
    ActionsBar,
    GDialog,
    TryOn,
    RootConfiguration,
    SlideTransition,
    GuideCarousel,
    CardHeader,
  },
  props: {
    defaultVariant: {
      type: Object,
      default: () => ({}),
    },
    defaultDemo: {
      type: Boolean,
      default: () => false,
    },
    defaultTryOn: {
      type: Boolean,
      default: () => false,
    },
    defaultShowSwitchMode: {
      type: Boolean,
      default: () => false,
    },
    defaultForceRefresh: {
      type: Boolean,
      default: () => false,
    },
  },
  emits: [
    'addToCart',
    'addToFavorite',
    'addToMyList',
    'shareBySns',
    'shareByLine',
    'showMyList',
    'showPages',
    'showChatbot',
  ],
  setup(props, { emit }) {
    // *: Define var
    const state = reactive({
      currentConfig: '',
      isShow: false,
      isTryOn: props.defaultTryOn,
      isConfirmReset: false,
      eventType: '',
      // favorState: '',
      // triggerBuild: false,
      isGuide: false,
    })

    const eventBus = useEvents()
    const { pushGtm } = useGtm()
    const { isDesktop } = useDevices()
    const [isDialogShare, setDialogShare] = useToggle(false)

    const trackingRefs = reactive({
      layoutRef: null as any,
      tryOnRef: null as any,
      refAddToCart: null as any,
    })

    // *: Define use
    const { storeFavorites, getMarkRead, markRead, handleCopyContent, openNewUrl } = useEnhancer()
    const customization = useCustomization()
    const toast = useToast()

    // *: Define computed
    const actionButtons = computed<AnyObject[]>(() => {
      const model = storeFavorites.selectedModel
      const len = storeFavorites.selectedLen
      // isEmpty(len)
      return customization.getNavConfigurations.value
        .map((item) => {
          const mustBeModel = isEmpty(model)
          const defaultDisabled = !!item.disabled
          if (item.type === actionTypes.MODEL)
            return item
          if (item.type === actionTypes.LENOPTIONS) {
            const mustBeLen = !isEmpty(len)
            return {
              ...item,
              disabled: defaultDisabled || (mustBeLen ? mustBeModel : true),
            }
          }
          return {
            ...item,
            disabled: defaultDisabled || mustBeModel,
          }
        })
        .sort((a, b) => a.order - b.order)
    })

    const isDemoMode = computed(() => customization.getCurrentDemoMode.value)

    const contrastStyle = computed(() => {
      return state.isTryOn ? 'white' : 'dark'
    })

    const dialogClass = computed(() => {
      return `dialog-end relative-position z-over-vr ${
        state.isTryOn ? 'dialog-grey' : 'dialog-blur'
      }`
    })

    const addToCartVariant = computed(() => storeFavorites.addToCartVariant)
    const addToMyListVariant = computed(() => storeFavorites.addToCartVariant.glassesVariant)

    // *: Define method & emit
    function showConfigType(type) {
      state.currentConfig = type
      state.isShow = true
    }

    function closeConfig() {
      state.currentConfig = ''
      state.isShow = false
    }

    const addToCart = async () => {
      const formData = await trackingRefs.tryOnRef.getFormDataThumbnail()
      trackingRefs.layoutRef.triggerBuildThumbnailUrl(formData, 'addToCart')
      pushGtm(trackingRefs.refAddToCart?.$el)
    }

    const addToMyList = async () => {
      const formData = await trackingRefs.tryOnRef.getFormDataThumbnail()
      trackingRefs.layoutRef.triggerBuildThumbnailUrl(formData, 'addToMyList')
    }

    const onShareSns = async () => {
      const formData = await trackingRefs.tryOnRef.getFormDataThumbnail()
      trackingRefs.layoutRef.triggerBuildThumbnailUrl(formData, 'shareBySns')
    }

    const onShareWithLine = async () => {
      const formData = await trackingRefs.tryOnRef.getFormDataThumbnail()
      trackingRefs.layoutRef.triggerBuildThumbnailUrl(
        formData,
        'shareWithLine',
      )
    }

    const buildShareByLineUrl = () => {
      const url = ShareSnsAdapter.transformShareByLine(addToMyListVariant.value)
      openNewUrl(url)
    }

    const buildShareByUrl = () => {
      const url = ShareSnsAdapter.transformShareByURL(addToMyListVariant.value)
      return handleCopyContent(url)
    }

    const onDone = (eventType: string) => {
      if (eventType === 'addToCart')
        return emit('addToCart', storeFavorites.addToCartVariant)

      if (eventType === 'addToMyList')
        return emit('addToMyList', storeFavorites.addToCartVariant)

      if (eventType === 'shareBySns') {
        isDemoMode.value && setDialogShare(true)
        return emit('shareBySns', {
          glassesVariant: addToMyListVariant.value,
        })
      }
      if (eventType === 'shareWithLine') {
        buildShareByLineUrl()
        return emit('shareByLine', {
          glassesVariant: addToMyListVariant.value,
        })
      }
    }

    const onShowMyList = (isDirty: boolean) => {
      emit('showMyList', isDirty)
    }

    const onShowPages = () => {
      emit('showPages')
    }

    const onShowChatbot = () => {
      emit('showChatbot')
    }

    const onScreenShot = async () => {
      const dataUrl: string = await trackingRefs.tryOnRef.getMainDataUrl()
      downloadImage(dataUrl)
    }

    const buildShareQuestionnaireUrl = () => {
      window.open(QUESTIONNAIRE.uri, '_blank')
        || window.location.assign(QUESTIONNAIRE.uri)
    }

    const onError = () => {
      toast.error('サムネイルURLを生成できません。もう一度やり直してください')
    }

    const resetDefault = () => {
      state.isConfirmReset = !state.isConfirmReset
    }

    const handleResetDefault = async () => {
      // *: Force reset store first
      resetDefault()
      customization.setInitialVariant(props.defaultVariant)
      storeFavorites.setInitial(customization.getDefaultVariant.value)
    }

    // *: Define life circle hooks
    onMounted(() => {
      state.isGuide = !getMarkRead()
      customization.setModelType(storeFavorites.selectedModel)
      eventBus.on(EventType.ADD_TO_MY_LIST, addToMyList)
      eventBus.on(EventType.RESET_DEFAULT, resetDefault)
      eventBus.on(EventType.SHARE_BY_SNS, onShareSns)
      eventBus.on(EventType.SHOW_PAGES, onShowPages)
      eventBus.on(EventType.SHOW_CHATBOT, onShowChatbot)
      eventBus.on(EventType.SHOW_MY_LIST, onShowMyList)
      eventBus.on(EventType.SCREEN_SHOT, onScreenShot)
      eventBus.on(EventType.SHARE_WITH_LINE, onShareWithLine)
      eventBus.on(EventType.SHARE_QUESTIONNAIRE, buildShareQuestionnaireUrl)
    })

    onBeforeUnmount(() => {
      eventBus.off(EventType.ADD_TO_MY_LIST)
      eventBus.off(EventType.RESET_DEFAULT)
      eventBus.off(EventType.SHARE_BY_SNS)
      eventBus.off(EventType.SHOW_MY_LIST)
      eventBus.off(EventType.SHOW_PAGES)
      eventBus.off(EventType.SHOW_CHATBOT)
      eventBus.off(EventType.SCREEN_SHOT)
      eventBus.off(EventType.SHARE_WITH_LINE)
      eventBus.off(EventType.SHARE_QUESTIONNAIRE)
    })

    const setDefaultVariant = (newData) => {
      const isFounded = isEmpty(newData)
      if (!isFounded) {
        const defaultConfig = Object.keys(newData).reduce((acc, key) => {
          const dataValue = newData[key]
          return { ...acc, [key]: dataValue }
        }, {})
        storeFavorites.setInitial(defaultConfig)
      }
    }

    const handleNextGuide = () => {
      markRead()
      state.isGuide = false
    }

    const handleCloseDialogShare = () => {
      setDialogShare(false)
    }

    // ? Set default config when store empty
    watch(customization.getDefaultVariant, setDefaultVariant)

    watch(
      () => props.defaultVariant,
      (newValue) => {
        customization.setInitialVariant(newValue)
      },
      {
        immediate: true,
      },
    )

    watch(
      () => props.defaultDemo,
      (newValue) => {
        customization.setInitialDemoMode(newValue)
      },
      {
        immediate: true,
      },
    )

    watch(
      () => props.defaultShowSwitchMode,
      (newValue) => {
        customization.setStatusSwitchMode(newValue)
      },
      {
        immediate: true,
      },
    )

    watch(
      () => props.defaultForceRefresh,
      (newValue) => {
        customization.setStatusForceRefresh(newValue)
      },
      {
        immediate: true,
      },
    )

    return {
      ...toRefs(state),
      ...toRefs(trackingRefs),
      isDialogShare,
      actionButtons,
      actionTypes,
      addToCartVariant,
      addToMyListVariant,
      showConfigType,
      closeConfig,
      isDesktop,
      isDemoMode,
      addToCart,
      resetDefault,
      handleResetDefault,
      handleNextGuide,
      dialogClass,
      contrastStyle,
      onDone,
      onError,
      handleCloseDialogShare,
      buildShareByUrl,
    }
  },
})
</script>

<template>
  <Layout
    ref="layoutRef"
    :is-back="isConfirmReset || isGuide"
    @done="onDone"
    @error="onError"
  >
    <template #header>
      <ToolBar
        v-model="isTryOn"
        :is-back="isConfirmReset || isGuide || isShow"
        :contrast-style="contrastStyle"
      />
    </template>
    <TryOn ref="tryOnRef" :is-guide="isGuide" :is-try-on="isTryOn" :is-back="isConfirmReset || isGuide" />
    <template #footer>
      <section v-show="!isShow" class="full-width">
        <ZoomTool :is-try-on="isTryOn" />
        <ActionsBar
          :action-buttons="actionButtons"
          :contrast-style="contrastStyle"
          @set="showConfigType"
        />
      </section>
    </template>
    <GDialog
      v-model="isShow"
      width="100%"
      transition="custom-from-bottom-transition"
      local
      :content-class="dialogClass"
    >
      <SlideTransition enter="fadeIn" leave="fadeOut">
        <RootConfiguration :type-schema="currentConfig" @back="closeConfig" />
      </SlideTransition>
    </GDialog>

    <!-- Confirm dialog -->
    <GDialog
      v-model="isConfirmReset"
      width="100%"
      persistent
      local
      content-class="dialog-white absolute-center dialog-width"
    >
      <q-card>
        <q-card-section>
          <div class="text-h6">
            確認
          </div>
        </q-card-section>

        <q-card-section class="q-pt-none">
          眼鏡の設定をリセットしますか？
        </q-card-section>

        <q-card-actions align="right">
          <q-btn
            flat
            label="いいえ"
            padding="none md"
            no-caps
            @click="resetDefault"
          />
          <q-btn
            label="はい"
            padding="none md"
            color="primary"
            no-caps
            @click="handleResetDefault"
          />
        </q-card-actions>
      </q-card>
    </GDialog>

    <!-- Guide dialog -->
    <GDialog
      v-model="isGuide"
      width="100%"
      persistent
      local
      content-class="dialog-white absolute-center dialog-carousel"
    >
      <GuideCarousel @done="handleNextGuide" />
    </GDialog>

    <!-- URI share dialog -->
    <GDialog
      v-model="isDialogShare"
      width="100%"
      persistent
      local
      content-class="dialog-white absolute-center dialog-share"
    >
      <q-card>
        <CardHeader
          title="シェアする"
          hidden-left
          @close="handleCloseDialogShare"
        />
        <q-card-section class="q-mx-lg">
          <q-btn
            class="full-width"
            rounded
            outline
            label="URLをコピーする"
            icon="content_copy"
            @click="buildShareByUrl"
          />
        </q-card-section>
      </q-card>
    </GDialog>
  </Layout>
</template>
